import { Component, OnInit, OnDestroy, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../core/pipes/translate.pipe';
import { Subscription } from 'rxjs';
import { FooterService, FooterData } from '../../services/footer.service';
import { LanguageService } from '../../../core/services/language.service';

@Component({
  selector: 'app-footer',
  imports: [CommonModule, TranslatePipe],
  templateUrl: './footer.html',
  styleUrl: './footer.scss',
})
export class Footer implements OnInit, OnDestroy {
  footerData: FooterData | null = null;
  isLoading = false;
  errorMessage = '';

  // signal يضمن إن Angular يعيد رسم الـ template فوراً عند أي تغيير
  readonly lang = signal<string>('ar');

  private langSub: Subscription | null = null;

  constructor(
    private footerService: FooterService,
    private languageService: LanguageService,
  ) {}

  ngOnInit(): void {
    // تعيين اللغة الحالية فوراً
    this.lang.set(this.languageService.getCurrentLanguage());

    // الاشتراك في تغييرات اللغة — signal يجبر Angular على re-render تلقائياً
    this.langSub = this.languageService.currentLanguage$.subscribe((newLang) => {
      console.log('🦶 Footer lang changed:', newLang);
      this.lang.set(newLang);
    });

    this.loadFooterData();
  }

  ngOnDestroy(): void {
    this.langSub?.unsubscribe();
  }

  loadFooterData(): void {
    this.isLoading = true;
    this.errorMessage = '';

    this.footerService.getFooterData().subscribe({
      next: (response) => {
        const raw = response.footer;
        if (Array.isArray(raw)) {
          this.footerData = raw[0] ?? null;
        } else if (raw && typeof raw === 'object') {
          this.footerData = raw as unknown as FooterData;
        }

        // إزالة التكرار في social links
        if (this.footerData?.socialLinks) {
          const seen = new Set<string>();
          this.footerData.socialLinks = this.footerData.socialLinks.filter((s) => {
            const key = s.link ?? s.icon;
            if (seen.has(key)) return false;
            seen.add(key);
            return true;
          });
        }

        this.isLoading = false;
      },
      error: (error) => {
        console.error('❌ Error loading footer:', error);
        this.errorMessage = 'حدث خطأ أثناء تحميل بيانات الفوتر';
        this.isLoading = false;
      },
    });
  }

  getSocialIcon(iconName: string): string {
    const iconMap: { [key: string]: string } = {
      facebook: 'fab fa-facebook-f',
      twitter: 'fab fa-twitter',
      instagram: 'fab fa-instagram',
      linkedin: 'fab fa-linkedin-in',
      youtube: 'fab fa-youtube',
      tiktok: 'fab fa-tiktok',
      whatsapp: 'fab fa-whatsapp',
      telegram: 'fab fa-telegram-plane',
      snapchat: 'fab fa-snapchat-ghost',
      foo: 'fab fa-facebook-f',
    };
    return iconMap[iconName.toLowerCase()] || 'fas fa-link';
  }

  getQuickLinkIcon(linkTitle: string): string {
    const iconMap: { [key: string]: string } = {
      home: 'fas fa-home',
      الرئيسية: 'fas fa-home',
      rent: 'fas fa-key',
      تأجير: 'fas fa-key',
      buy: 'fas fa-shopping-cart',
      شراء: 'fas fa-shopping-cart',
      projects: 'fas fa-building',
      about: 'fas fa-info-circle',
      'about us': 'fas fa-info-circle',
      contact: 'fas fa-phone',
      'contact us': 'fas fa-phone',
      services: 'fas fa-cogs',
      properties: 'fas fa-home',
    };
    return iconMap[linkTitle.toLowerCase()] || 'fas fa-chevron-right';
  }

  getPolicyIcon(policyLabel: string): string {
    const iconMap: { [key: string]: string } = {
      'terms & conditions': 'fas fa-file-contract',
      'terms and conditions': 'fas fa-file-contract',
      'privacy policy': 'fas fa-shield-alt',
      'cookie policy': 'fas fa-cookie-bite',
      'refund policy': 'fas fa-undo-alt',
      disclaimer: 'fas fa-exclamation-triangle',
    };
    return iconMap[policyLabel.toLowerCase()] || 'fas fa-file-alt';
  }
}
